import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { MonthlyReportData, TransactionWithCategory } from "@/hooks/use-reports";
import type { DreSection, DreTotals } from "@/hooks/use-dre";

// Extend jsPDF type for autoTable
declare module "jspdf" {
  interface jsPDF {
    lastAutoTable: { finalY: number };
  }
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);

export function exportTransactionsToCSV(
  transactions: TransactionWithCategory[],
  filename: string
): void {
  const headers = ["Data", "Descrição", "Categoria", "Conta", "Tipo", "Valor"];
  
  const rows = transactions.map(t => [
    format(new Date(t.transaction_date), "dd/MM/yyyy"),
    t.description,
    t.category?.name || "Sem categoria",
    t.account?.name || "N/A",
    t.type === "income" ? "Receita" : "Despesa",
    formatCurrency(Number(t.amount)),
  ]);

  const csvContent = [
    headers.join(";"),
    ...rows.map(row => row.join(";")),
  ].join("\n");

  // Add BOM for proper UTF-8 encoding in Excel
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
  
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

export function exportReportToPDF(report: MonthlyReportData): void {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  let yPosition = 20;

  // Title
  doc.setFontSize(20);
  doc.setTextColor(34, 197, 94); // Green
  doc.text("Relatório Financeiro", margin, yPosition);
  
  yPosition += 10;
  doc.setFontSize(14);
  doc.setTextColor(100);
  const monthLabel = report.monthLabel.charAt(0).toUpperCase() + report.monthLabel.slice(1);
  doc.text(monthLabel, margin, yPosition);

  // Summary section
  yPosition += 15;
  doc.setFontSize(12);
  doc.setTextColor(0);
  doc.text("Resumo do Mês", margin, yPosition);

  yPosition += 8;
  doc.setFontSize(10);
  doc.setTextColor(60);

  // Summary box
  const summaryData = [
    ["Total de Receitas", formatCurrency(report.totalIncome)],
    ["Total de Despesas", formatCurrency(report.totalExpenses)],
    ["Saldo do Mês", formatCurrency(report.balance)],
    ["Número de Transações", report.transactions.length.toString()],
  ];

  autoTable(doc, {
    startY: yPosition,
    head: [],
    body: summaryData,
    theme: "plain",
    styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 60 },
      1: { halign: "right" },
    },
    margin: { left: margin },
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Comparison with previous month
  doc.setFontSize(12);
  doc.setTextColor(0);
  doc.text("Comparativo com Mês Anterior", margin, yPosition);

  yPosition += 8;
  const comparisonData = [
    [
      "Receitas",
      `${report.previousMonthComparison.incomeChangePercent >= 0 ? "+" : ""}${report.previousMonthComparison.incomeChangePercent.toFixed(1)}%`,
      formatCurrency(report.previousMonthComparison.incomeChange),
    ],
    [
      "Despesas",
      `${report.previousMonthComparison.expenseChangePercent >= 0 ? "+" : ""}${report.previousMonthComparison.expenseChangePercent.toFixed(1)}%`,
      formatCurrency(report.previousMonthComparison.expenseChange),
    ],
  ];

  autoTable(doc, {
    startY: yPosition,
    head: [["Categoria", "Variação %", "Valor"]],
    body: comparisonData,
    theme: "striped",
    headStyles: { fillColor: [34, 197, 94] },
    styles: { fontSize: 9 },
    margin: { left: margin },
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Category breakdown
  if (report.categorySummaries.length > 0) {
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text("Gastos por Categoria", margin, yPosition);

    yPosition += 8;
    const categoryData = report.categorySummaries.slice(0, 10).map(cat => [
      cat.name,
      formatCurrency(cat.totalAmount),
      `${cat.percentage.toFixed(1)}%`,
      cat.transactionCount.toString(),
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [["Categoria", "Total", "% do Total", "Transações"]],
      body: categoryData,
      theme: "striped",
      headStyles: { fillColor: [34, 197, 94] },
      styles: { fontSize: 9 },
      margin: { left: margin },
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // Check if we need a new page
  if (yPosition > 220) {
    doc.addPage();
    yPosition = 20;
  }

  // Spending patterns
  doc.setFontSize(12);
  doc.setTextColor(0);
  doc.text("Padrões de Gastos", margin, yPosition);

  yPosition += 8;
  const patternsData: string[][] = [];

  if (report.spendingPatterns.highestSpendingDay) {
    patternsData.push([
      "Dia com mais gastos",
      report.spendingPatterns.highestSpendingDay.dayName,
      formatCurrency(report.spendingPatterns.highestSpendingDay.totalSpent),
    ]);
  }

  patternsData.push([
    "Total dias úteis",
    `${((report.spendingPatterns.weekdayVsWeekend.weekdayTotal / (report.totalExpenses || 1)) * 100).toFixed(0)}%`,
    formatCurrency(report.spendingPatterns.weekdayVsWeekend.weekdayTotal),
  ]);

  patternsData.push([
    "Total fim de semana",
    `${((report.spendingPatterns.weekdayVsWeekend.weekendTotal / (report.totalExpenses || 1)) * 100).toFixed(0)}%`,
    formatCurrency(report.spendingPatterns.weekdayVsWeekend.weekendTotal),
  ]);

  autoTable(doc, {
    startY: yPosition,
    head: [["Padrão", "Info", "Valor"]],
    body: patternsData,
    theme: "striped",
    headStyles: { fillColor: [34, 197, 94] },
    styles: { fontSize: 9 },
    margin: { left: margin },
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Top expenses
  if (report.spendingPatterns.largestTransactions.length > 0) {
    if (yPosition > 230) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text("Maiores Despesas", margin, yPosition);

    yPosition += 8;
    const topExpensesData = report.spendingPatterns.largestTransactions.slice(0, 5).map(t => [
      format(new Date(t.transaction_date), "dd/MM"),
      t.description.substring(0, 30),
      t.category?.name || "N/A",
      formatCurrency(Number(t.amount)),
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [["Data", "Descrição", "Categoria", "Valor"]],
      body: topExpensesData,
      theme: "striped",
      headStyles: { fillColor: [34, 197, 94] },
      styles: { fontSize: 9 },
      margin: { left: margin },
    });
  }

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Gerado em ${format(new Date(), "dd/MM/yyyy 'às' HH:mm")} - Página ${i} de ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: "center" }
    );
  }

  // Save
  const filename = `relatorio-${format(report.month, "yyyy-MM")}.pdf`;
  doc.save(filename);
}

// ─── DRE Export Functions ────────────────────────────────────────────

export function exportDreToCSV(
  sections: DreSection[],
  totals: DreTotals,
  periodLabel: string
): void {
  const headers = ["Descricao", "Valor", "% Receita"];
  const rows: string[][] = [];
  const receitaBruta = totals.receita_bruta;

  const pctStr = (value: number) =>
    receitaBruta > 0 ? `${((value / receitaBruta) * 100).toFixed(1)}%` : "-";

  // Revenue sections
  const revenueSections = sections.filter(
    (s) => s.section === "receita_operacional" || s.section === "outras_receitas"
  );
  for (const section of revenueSections) {
    rows.push([section.label, formatCurrency(section.total), pctStr(section.total)]);
    for (const cat of section.categories) {
      rows.push([`  ${cat.category_name}`, formatCurrency(cat.amount), pctStr(cat.amount)]);
    }
  }
  rows.push(["= RECEITA BRUTA", formatCurrency(totals.receita_bruta), "100.0%"]);
  rows.push([]);

  // Cost sections
  const costSections = sections.filter((s) => s.section === "custo_operacional");
  for (const section of costSections) {
    rows.push([`(-) ${section.label}`, formatCurrency(section.total), pctStr(section.total)]);
    for (const cat of section.categories) {
      rows.push([`  ${cat.category_name}`, formatCurrency(cat.amount), pctStr(cat.amount)]);
    }
  }
  rows.push(["= LUCRO BRUTO", formatCurrency(totals.lucro_bruto), pctStr(totals.lucro_bruto)]);
  rows.push([]);

  // OpEx sections
  const opexKeys = ["despesa_administrativa", "despesa_comercial", "despesa_pessoal", "outras_despesas"];
  const opexSections = sections.filter((s) => opexKeys.includes(s.section));
  for (const section of opexSections) {
    rows.push([`(-) ${section.label}`, formatCurrency(section.total), pctStr(section.total)]);
    for (const cat of section.categories) {
      rows.push([`  ${cat.category_name}`, formatCurrency(cat.amount), pctStr(cat.amount)]);
    }
  }
  rows.push([
    "= RESULTADO OPERACIONAL",
    formatCurrency(totals.resultado_operacional),
    pctStr(totals.resultado_operacional),
  ]);
  rows.push([]);

  // Financial sections
  const finExpSections = sections.filter((s) => s.section === "despesa_financeira");
  for (const section of finExpSections) {
    rows.push([`(-) ${section.label}`, formatCurrency(section.total), pctStr(section.total)]);
    for (const cat of section.categories) {
      rows.push([`  ${cat.category_name}`, formatCurrency(cat.amount), pctStr(cat.amount)]);
    }
  }
  const finIncSections = sections.filter((s) => s.section === "receita_financeira");
  for (const section of finIncSections) {
    rows.push([`(+) ${section.label}`, formatCurrency(section.total), pctStr(section.total)]);
    for (const cat of section.categories) {
      rows.push([`  ${cat.category_name}`, formatCurrency(cat.amount), pctStr(cat.amount)]);
    }
  }
  rows.push([
    "= RESULTADO LIQUIDO",
    formatCurrency(totals.resultado_liquido),
    pctStr(totals.resultado_liquido),
  ]);

  const csvContent = [
    `DRE - ${periodLabel}`,
    "",
    headers.join(";"),
    ...rows.map((row) => row.join(";")),
  ].join("\n");

  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `dre-${periodLabel.replace(/\s+/g, "-").toLowerCase()}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

export function exportDreToPDF(
  sections: DreSection[],
  totals: DreTotals,
  periodLabel: string
): void {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  let yPosition = 20;

  const receitaBruta = totals.receita_bruta;
  const pctStr = (value: number) =>
    receitaBruta > 0 ? `${((value / receitaBruta) * 100).toFixed(1)}%` : "-";

  // Title
  doc.setFontSize(20);
  doc.setTextColor(34, 197, 94);
  doc.text("DRE - Demonstrativo de Resultados", margin, yPosition);

  yPosition += 10;
  doc.setFontSize(14);
  doc.setTextColor(100);
  doc.text(periodLabel, margin, yPosition);

  yPosition += 15;

  // Build table rows
  const tableRows: string[][] = [];

  // Revenue
  const revenueSections = sections.filter(
    (s) => s.section === "receita_operacional" || s.section === "outras_receitas"
  );
  for (const section of revenueSections) {
    tableRows.push([section.label, formatCurrency(section.total), pctStr(section.total)]);
    for (const cat of section.categories) {
      tableRows.push([`    ${cat.category_name}`, formatCurrency(cat.amount), pctStr(cat.amount)]);
    }
  }
  tableRows.push(["= RECEITA BRUTA", formatCurrency(totals.receita_bruta), "100.0%"]);

  // Costs
  const costSections = sections.filter((s) => s.section === "custo_operacional");
  for (const section of costSections) {
    tableRows.push([`(-) ${section.label}`, formatCurrency(section.total), pctStr(section.total)]);
    for (const cat of section.categories) {
      tableRows.push([`    ${cat.category_name}`, formatCurrency(cat.amount), pctStr(cat.amount)]);
    }
  }
  tableRows.push(["= LUCRO BRUTO", formatCurrency(totals.lucro_bruto), pctStr(totals.lucro_bruto)]);

  // OpEx
  const opexKeys = ["despesa_administrativa", "despesa_comercial", "despesa_pessoal", "outras_despesas"];
  const opexSections = sections.filter((s) => opexKeys.includes(s.section));
  for (const section of opexSections) {
    tableRows.push([`(-) ${section.label}`, formatCurrency(section.total), pctStr(section.total)]);
    for (const cat of section.categories) {
      tableRows.push([`    ${cat.category_name}`, formatCurrency(cat.amount), pctStr(cat.amount)]);
    }
  }
  tableRows.push([
    "= RESULTADO OPERACIONAL",
    formatCurrency(totals.resultado_operacional),
    pctStr(totals.resultado_operacional),
  ]);

  // Financial
  const finExpSections = sections.filter((s) => s.section === "despesa_financeira");
  for (const section of finExpSections) {
    tableRows.push([`(-) ${section.label}`, formatCurrency(section.total), pctStr(section.total)]);
    for (const cat of section.categories) {
      tableRows.push([`    ${cat.category_name}`, formatCurrency(cat.amount), pctStr(cat.amount)]);
    }
  }
  const finIncSections = sections.filter((s) => s.section === "receita_financeira");
  for (const section of finIncSections) {
    tableRows.push([`(+) ${section.label}`, formatCurrency(section.total), pctStr(section.total)]);
    for (const cat of section.categories) {
      tableRows.push([`    ${cat.category_name}`, formatCurrency(cat.amount), pctStr(cat.amount)]);
    }
  }
  tableRows.push([
    "= RESULTADO LIQUIDO",
    formatCurrency(totals.resultado_liquido),
    pctStr(totals.resultado_liquido),
  ]);

  autoTable(doc, {
    startY: yPosition,
    head: [["Descricao", "Valor", "% Receita"]],
    body: tableRows,
    theme: "striped",
    headStyles: { fillColor: [34, 197, 94] },
    styles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { halign: "right", cellWidth: 45 },
      2: { halign: "right", cellWidth: 30 },
    },
    margin: { left: margin },
    didParseCell: (data: any) => {
      // Bold total rows
      const text = data.cell?.text?.[0] || "";
      if (text.startsWith("=")) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [240, 240, 240];
      }
    },
  });

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Gerado em ${format(new Date(), "dd/MM/yyyy 'as' HH:mm")} - Pagina ${i} de ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: "center" }
    );
  }

  doc.save(`dre-${periodLabel.replace(/\s+/g, "-").toLowerCase()}.pdf`);
}
